import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import React, { useEffect, useState } from "react";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import { Field, FormRenderProps } from "react-final-form";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import { FV } from "@/components/Form/Validation/FormValidators";
import classNames from "classnames";
import i18n from "@/i18n";
import "./VacancyContractForm.scss";
import BFDate from "@/modules/abstract-ui/forms/date/BFDate";
import { RentalUnit } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import { AssetTypes } from "@/model/AssetTypes";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import { LETTER_HEADER_TEMPLATE } from "@/apps/tatar/activityApp/specific/tenantCommunication/startTenantCommunication/form/APStartTenantCommunicationStep1TemplateType";
import BFAssetChooserSelect from "@/modules/abstract-ui/forms/chooser/BFAssetChooserSelect";
import { TemplateAsset } from "@/modules/template-module/TemplateInterfaces";
import MQ from "@/utils/MatchQueryUtils";
import { getVacancyContractAddressHtml } from "./paragraphs/VacancyContractAddress";
import VacancyContractPreview from "./VacancyContractPreview";
import ReactDOMServer from "react-dom/server";
import StringUtils from "@/utils/StringUtils";
import { getVacancyContractSignatureHTML } from "./paragraphs/VacancyContractSignature";
import VacancyContract from "./paragraphs/VacancyContract";
import BFSelect from "@/modules/abstract-ui/forms/select/BFSelect";
import VacancyContractFormTenantPart from "./VacancyContractFormTenantPart";
import { VacancyActivityMeta } from "@/apps/tatar/activityApp/specific/vacancy/model/VacancyActivity.interface";
import {
  VacancyApplication,
  VacancyContractFormType,
} from "@/apps/tatar/activityApp/specific/vacancy/model/VacancyApplicant.interface";
import _ from "lodash";
import VacancyContractCancelButton from "../VacancyContractCancelButton";
import VacancyContractUtils from "../../../../vacancy-applicant/utils/VacancyContractUtils";

type Props = {
  activity: APActivity;
  object: OAObject;
  rentalUnit: RentalUnit;
  application: VacancyApplication;
  onSubmit: (values: any) => void;
  replacements: any;
  currentState: VacancyContractFormType | undefined;
};

const VacancyContractForm = (props: Props) => {
  const {
    activity,
    object,
    application,
    rentalUnit,
    onSubmit,
    replacements,
    currentState,
  } = props;

  const [currentFieldEditing, setCurrentFieldEditing] = useState<string | null>(
    null
  );

  const [initialValues, setInitialValues] = useState<
    Partial<VacancyContractFormType> | undefined
  >();

  useEffect(() => {
    if (!_.isEmpty(currentState)) {
      setInitialValues(currentState);
      return;
    }
    const activityMeta = activity.data.meta as VacancyActivityMeta;
    const [contractPerson] = application.data.applicants;

    const objectAdr = object?.data?.feature?.address?.address?.find(
      (a) => a.isMain
    );
    const objectAddress = objectAdr
      ? `${objectAdr.streetname} ${objectAdr.streetnumber}`
      : "";
    const initialState: Partial<VacancyContractFormType> = {
      objectAddress: rentalUnit.data.building || objectAddress,
      tenants: application.data.applicants.sort((a, b) => {
        if (a.isMain === b.isMain) return 0;
        return a.isMain ? -1 : 1;
      }),
      moveInDate: application.data.moveIn,
      rentalUnitAdditionalCosts: activityMeta.additionalCosts,
      rentalUnitRooms: undefined,
      rentalUnitKitchen: undefined,
      rentalUnitBathrooms: undefined,
      heatingCostsInclusive:
        activityMeta.heatingCostsIncluded === "yes"
          ? true
          : activityMeta.heatingCostsIncluded === "no"
          ? false
          : undefined,
      rentalUnitHallway: undefined,
      bankUsageReference: `Miete, Objekt ${object.data.id}, ME ${
        rentalUnit.data.id
      }, Mieter ${contractPerson?.firstName || "-"}  ${
        contractPerson?.lastName || "-"
      }`,
      rentalUnitRentBase: activityMeta.planRent,
      rentalUnitRentTotal: activityMeta.planRent,
      templateContent: "",
      templatePrefix: getVacancyContractAddressHtml(
        application.data.applicants.length
      ),
      templateSuffix: getVacancyContractSignatureHTML({
        applicantCount: application.data.applicants.length,
      }),
    };

    setInitialValues(initialState);
    // disabled because all values proposed here are covered by those ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object._id, activity._id, application._id, rentalUnit._id, replacements]);

  const renderPreviewComponents = () => {
    return (
      <>
        <VacancyContract />
      </>
    );
  };

  return (
    <div className="vacancy-applicant-contract-form">
      <FormStruct
        additionalActions={<VacancyContractCancelButton />}
        submitText={i18n.t("Global.Buttons.continue", "Weiter")}
        noPadding
        initialValues={initialValues}
        onSubmit={(data: VacancyContractFormType) => {
          const templateContent = ReactDOMServer.renderToString(
            renderPreviewComponents()
          );
          const values: VacancyContractFormType = {
            ...data,
            templateContent,
          };
          onSubmit(values);
        }}
        render={(formRenderProps: FormRenderProps<VacancyContractFormType>) => {
          return (
            <div className="contract-form-content __flex-row">
              <div className="contract-form-fields __flex-1 padding-bottom-30">
                <div className="footer-form-part form-section">
                  <div className="__h3">
                    {i18n.t(
                      "apTemplate:Vacancy.Applicant.Contract.Label.DocumentProps",
                      "Dokument"
                    )}
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field name="letterTemplateId" validate={FV.required()}>
                      {({ input, meta }) => (
                        <BFAssetChooserSelect
                          assetType={AssetTypes.Template}
                          overlayHeight={300}
                          matchQuery={MQ.and(
                            MQ.eq("data.type", activity.data.type),
                            MQ.eq("data.templateType", LETTER_HEADER_TEMPLATE)
                          )}
                          getOption={(node: TemplateAsset) => ({
                            label: node.data.name,
                            value: node._id,
                          })}
                          disabled={false}
                          label={
                            i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.DocumentFooter",
                              "Fußzeile"
                            ) + "*"
                          }
                          {...input}
                          {...FV.getValidation(meta)}
                        />
                      )}
                    </Field>
                  </div>
                </div>

                <VacancyContractFormTenantPart
                  setCurrentFieldEditing={setCurrentFieldEditing}
                />
                <div className="bank-form-part form-section">
                  <div className="__h3">
                    {i18n.t(
                      "apTemplate:Vacancy.Applicant.Contract.Label.BankPart",
                      "Bank Daten"
                    )}
                  </div>
                  <Field name="bankUsageReference">
                    {({ input, meta }) => (
                      <div className={`__field`}>
                        <BFInput
                          label={i18n.t(
                            "apTemplate:Vacancy.Applicant.Contract.Label.BankUsage",
                            "Verwendungszweck"
                          )}
                          {...input}
                          {...FV.getValidation(meta)}
                          onFocus={() => {
                            setCurrentFieldEditing(input.name);
                            input.onFocus();
                          }}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className="rental-unit-form-part form-section">
                  <div className="__h3">
                    {i18n.t(
                      "apTemplate:Vacancy.Applicant.Contract.Label.RentalUnit",
                      "Mietsache"
                    )}
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field name="objectAddress">
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.objectAddress",
                              "Objektadresse"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <Field name="moveInDate">
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFDate
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.MoveIn",
                              "Einzugsdatum"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>

                  <div className={classNames("__flex-row __justify-between")}>
                    <Field
                      name="rentalUnitRooms"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.Rooms",
                              "Anzahl Räume"
                            )}
                            type="number"
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <Field
                      name="rentalUnitKitchen"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.Kitchens",
                              "Anzahl Küchen"
                            )}
                            type="number"
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field
                      name="rentalUnitBathrooms"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.Bathrooms",
                              "Anzahl Badezimmer"
                            )}
                            type="number"
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <Field
                      name="rentalUnitHallway"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            type="number"
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.Hallways",
                              "Anzahl Flure"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <div className="rental-unit-rent-cost-form-part form-section">
                  <div className="__h3">
                    {i18n.t(
                      "apTemplate:Vacancy.Applicant.Contract.Label.RentCost",
                      "Miete / Nebenkosten"
                    )}
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field
                      name="rentalUnitRentBase"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.RentPlanned",
                              "Miete"
                            )}
                            type="priceInput"
                            currencySuffix={StringUtils.getCurrencySymbol(
                              "EUR"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <Field
                      name="rentalUnitAdditionalCosts"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            type="priceInput"
                            currencySuffix={StringUtils.getCurrencySymbol(
                              "EUR"
                            )}
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.RentAdditionalCosts",
                              "Nebenkosten"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field
                      name="heatingCostsInclusive"
                      validate={FV.compose(FV.required(), FV.min(1))}
                    >
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFSelect
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.HeatingCostsInclusive",
                              "Heizkosten inklusive"
                            )}
                            data={[
                              {
                                label: i18n.t("Global.Yes", "Ja"),
                                value: "1",
                              },
                              {
                                label: i18n.t("Global.No", "Nein"),
                                value: "0",
                              },
                            ]}
                            value={
                              input.value === true
                                ? "1"
                                : input.value === false
                                ? "0"
                                : undefined
                            }
                            onChange={(value: string) => {
                              const boolValue = value === "1" ? true : false;
                              input.onChange(boolValue);
                            }}
                            h
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <FormFieldValues
                  names={["rentalUnitAdditionalCosts", "rentalUnitRentBase"]}
                >
                  {([rentalUnitAdditionalCosts, rentalUnitRentBase]) => {
                    const additionalCosts =
                      Number(rentalUnitAdditionalCosts) || 0;
                    const rentBase = Number(rentalUnitRentBase) || 0;
                    const sum = additionalCosts + rentBase;
                    return (
                      <Field
                        name="rentalUnitRentTotal"
                        validate={FV.compose(FV.required(), FV.min(1))}
                      >
                        {({ input }) => {
                          // Update the field value if the sum has changed
                          if (input.value !== sum) {
                            input.onChange(sum);
                          }
                          // Return a hidden input (or null) since we don't need to render anything visible.
                          return <input type="hidden" value={sum} />;
                        }}
                      </Field>
                    );
                  }}
                </FormFieldValues>
              </div>
              <div className="contract-preview __flex-3">
                <FormFieldValues
                  names={[
                    "letterTemplateId",
                    "templateSuffix",
                    "templatePrefix",
                  ]}
                >
                  {([letterTemplateId, templateSuffix, templatePrefix]) => {
                    const tenants = VacancyContractUtils.getTenantReplacements(
                      formRenderProps.values
                    );

                    return (
                      <VacancyContractPreview
                        currentFieldEditing={currentFieldEditing}
                        resolvedVariables={{
                          ...tenants,
                          ..._.omit(formRenderProps.values, "tenants"),
                          ...replacements,
                        }}
                        conditions={{
                          heatingCostsInclusive:
                            formRenderProps.values.heatingCostsInclusive,
                        }}
                        letterTemplateId={letterTemplateId}
                        contentNodes={renderPreviewComponents()}
                        prefixHtml={templatePrefix}
                        suffixHtml={templateSuffix}
                      />
                    );
                  }}
                </FormFieldValues>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export default VacancyContractForm;
